import { FocusTrap } from 'focus-trap-react';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';

import { Portal } from '@xing-com/portal';

import { ContentBannerPrimitive } from './content-banner.primitive';
import {
  contentBannerDuration,
  contentBannerTransitionName,
} from './content-banner.styles';
import type { ContentBannerProps } from './content-banner.types';

export const ContentBanner = React.forwardRef<
  HTMLDivElement,
  ContentBannerProps
>(
  (
    {
      children,
      className,
      closeButtonProps,
      display = 'fixed',
      focusTrapProps,
      handleOnClose,
      noPadding,
      onExited,
      show = true,
      showCloseButton = true,
      variant = 'default',
      ...props
    },
    forwardedRef
  ): JSX.Element => {
    const bannerRef = React.useRef<HTMLDivElement>(null);

    const combinedRef = React.useMemo(() => {
      if (forwardedRef) {
        return typeof forwardedRef === 'function'
          ? { current: null, set: forwardedRef }
          : forwardedRef;
      }
      return bannerRef;
    }, [forwardedRef]);

    const timeout = display === 'fixed' ? parseInt(contentBannerDuration) : 0;

    const {
      focusTrapOptions: userFocusTrapOptions,
      active: userActive,
      ...otherFocusTrapProps
    } = focusTrapProps || {};

    const mergedFocusTrapOptions = {
      allowOutsideClick: true,
      escapeDeactivates: true,
      fallbackFocus: () => combinedRef.current!,
      returnFocusOnDeactivate: true,
      ...(userFocusTrapOptions || {}),
    };

    // Focus trap is disabled by default. Opt in via focusTrapProps.active
    const focusTrapActive = userActive ?? false;

    const contentBanner = (
      <ContentBannerPrimitive
        className={className}
        closeButtonProps={closeButtonProps}
        display={display}
        handleOnClose={handleOnClose}
        noPadding={noPadding}
        ref={combinedRef}
        showCloseButton={showCloseButton}
        tabIndex={-1}
        variant={variant}
        {...props}
      >
        {children}
      </ContentBannerPrimitive>
    );

    const contentBannerTransition = (
      <CSSTransition
        appear={show}
        classNames={contentBannerTransitionName}
        in={show}
        key="contentbanner"
        mountOnEnter
        onExited={onExited}
        timeout={timeout}
        unmountOnExit
        nodeRef={combinedRef}
      >
        <FocusTrap
          active={focusTrapActive && show && display === 'fixed'}
          {...otherFocusTrapProps}
          focusTrapOptions={mergedFocusTrapOptions}
        >
          {contentBanner}
        </FocusTrap>
      </CSSTransition>
    );

    if (display === 'fixed') {
      return <Portal>{contentBannerTransition}</Portal>;
    }

    return contentBannerTransition;
  }
);

ContentBanner.displayName = 'ContentBanner';
